/**
 * DRPX Courier — Realtime-Subscriptions (Supabase)
 *
 * Abonniert Postgres-Realtime-Events für Aufträge, Angebote und
 * Benachrichtigungen. Die Channels werden in useEffect-Cleanups
 * via `unsubscribe()` wieder entfernt, um Memory-Leaks zu vermeiden.
 *
 * Alle Funktionen sind synchron (kein `async`) — sie geben einen
 * `RealtimeChannel` zurück, der vom Caller im Cleanup aufgeräumt wird.
 * Bei fehlendem Supabase-Client wird `null` zurückgegeben — Caller
 * prüfen das mit `if (ch)` vor dem Cleanup.
 *
 * @module lib/supabase/realtime
 */

import { _sb } from './client.js';
import { mapNotificationFromDb, mapAuftragFromDb, mapAngebotFromDb } from './mappers.js';
import type { RealtimeChannel } from '@supabase/realtime-js';
import type { Auftrag, Angebot } from '../../types.js';

// ════════════════════════════════════════════════════════════════════
// Callback-Typen
// ════════════════════════════════════════════════════════════════════

/** Callback für neue/aktualisierte Aufträge. */
type AuftragCallback = (auftrag: Auftrag) => void;

/** Callback für gelöschte Aufträge (nur ID). */
type AuftragDeleteCallback = (id: string) => void;

/** Callback für neue/aktualisierte Angebote. */
type AngebotCallback = (angebot: Angebot) => void;

/**
 * Callback für gelöschte Angebote (ID + auftragId für Status-Reset).
 * `auftragId` kommt aus `p.old.auftrag_id` und kann fehlen, wenn die
 * Row nicht mehr existiert.
 */
type AngebotDeleteCallback = (id: string, auftragId?: string) => void;

/**
 * Callback für eingehende Benachrichtigungen.
 * Noch kein `Notification`-Typ in `types.ts` — wird `Record<string, unknown>`
 * bis das Notifications-Modul migriert ist.
 */
type NotificationCallback = (notification: Record<string, unknown> | null) => void;

// ════════════════════════════════════════════════════════════════════
// Payload-Typ (Supabase Realtime postgres_changes)
// ════════════════════════════════════════════════════════════════════

/**
 * Minimaler Payload-Typ für `postgres_changes`-Events.
 *
 * Supabase liefert `p.new` und `p.old` als unstrukturierte Records —
 * die Mapper (`mapAuftragFromDb` etc.) konvertieren sie in Domain-Typen.
 */
interface RealtimePayload {
  new: Record<string, unknown>;
  old: Record<string, unknown>;
}

// ════════════════════════════════════════════════════════════════════
// SUBSCRIPTIONS
// ════════════════════════════════════════════════════════════════════

/**
 * Abonniert Realtime-Events für eingehende Benachrichtigungen eines Users.
 * Der Filter sorgt dafür, dass nur Notifications für diesen User zugestellt
 * werden (zusätzlich zum RLS-Schutz auf der DB-Seite).
 *
 * @param userId UUID des Users
 * @param cb Callback, wird bei jeder neuen Benachrichtigung aufgerufen
 * @returns Supabase Channel (für späteres `unsubscribe`) oder `null`
 */
export function subscribeNotifications(
  userId: string,
  cb: NotificationCallback,
): RealtimeChannel | null {
  if (!_sb) return null;
  return _sb
    .channel('notif:' + userId)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: 'empfaenger_id=eq.' + userId,
      },
      function (p: RealtimePayload) {
        cb(mapNotificationFromDb(p.new));
      },
    )
    .subscribe();
}

/**
 * Abonniert Realtime-Events für die `auftraege`-Tabelle.
 *
 * @param onInsert Callback bei neuem Auftrag
 * @param onUpdate Callback bei Auftrag-Update
 * @param onDelete Callback bei Auftrag-Löschung
 * @returns Supabase Channel oder `null`
 */
export function subscribeAuftraege(
  onInsert: AuftragCallback | null,
  onUpdate: AuftragCallback | null,
  onDelete: AuftragDeleteCallback | null,
): RealtimeChannel | null {
  if (!_sb) return null;
  return _sb
    .channel('auftraege-realtime')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'auftraege' },
      function (p: RealtimePayload) {
        const mapped = mapAuftragFromDb(p.new);
        if (onInsert && mapped) onInsert(mapped);
      },
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'auftraege' },
      function (p: RealtimePayload) {
        const mapped = mapAuftragFromDb(p.new);
        if (onUpdate && mapped) onUpdate(mapped);
      },
    )
    .on(
      'postgres_changes',
      { event: 'DELETE', schema: 'public', table: 'auftraege' },
      function (p: RealtimePayload) {
        if (onDelete && p.old && typeof p.old.id === 'string') {
          onDelete(p.old.id);
        }
      },
    )
    .subscribe();
}

/**
 * Abonniert Realtime-Events für die `angebote`-Tabelle.
 *
 * @param onInsert Callback bei neuem Angebot
 * @param onUpdate Callback bei Angebot-Update
 * @param onDelete Callback bei Angebot-Löschung (ID + auftragId)
 * @returns Supabase Channel oder `null`
 */
export function subscribeAngebote(
  onInsert: AngebotCallback | null,
  onUpdate: AngebotCallback | null,
  onDelete: AngebotDeleteCallback | null,
): RealtimeChannel | null {
  if (!_sb) return null;
  return _sb
    .channel('angebote-realtime')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'angebote' },
      function (p: RealtimePayload) {
        const mapped = mapAngebotFromDb(p.new);
        if (onInsert && mapped) onInsert(mapped);
      },
    )
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'angebote' },
      function (p: RealtimePayload) {
        const mapped = mapAngebotFromDb(p.new);
        if (onUpdate && mapped) onUpdate(mapped);
      },
    )
    .on(
      'postgres_changes',
      { event: 'DELETE', schema: 'public', table: 'angebote' },
      function (p: RealtimePayload) {
        if (onDelete && p.old) {
          const id = typeof p.old.id === 'string' ? p.old.id : '';
          const auftragId =
            typeof p.old.auftrag_id === 'string' ? p.old.auftrag_id : undefined;
          if (id) onDelete(id, auftragId);
        }
      },
    )
    .subscribe();
}

// ════════════════════════════════════════════════════════════════════
// CLEANUP
// ════════════════════════════════════════════════════════════════════

/**
 * Beendet einen Realtime-Channel sauber (wichtig im useEffect-Cleanup,
 * sonst entstehen Memory-Leaks und doppelte Event-Handler).
 *
 * Akzeptiert `null` und `undefined` defensiv — Caller müssen keinen
 * eigenen Guard schreiben.
 *
 * @param ch Der von `subscribe*` zurückgegebene Supabase Channel
 */
export function unsubscribe(ch: RealtimeChannel | null | undefined): void {
  if (!ch || !_sb) return;
  try {
    _sb.removeChannel(ch);
  } catch {
    /* ignored — Channel war bereits entfernt oder Client disconnected */
  }
}
